import React from 'react'
import styled from 'styled-components'
import Text from '../../components/Text'
import Image from '../../components/Image'
import { SCREEN_SIZE, Media } from '../../lib/Media'
import CheckoutInfoBanner from '../../components/CheckoutInfoBanner/CheckoutInfoBanner'
import { TangramFairpriceProvider } from '../TangramFairpriceProvider'
import { NotificationBanner } from '@ntuctech/tangram-web'

export const StyledArticle = styled.article`
  margin-bottom: 1rem;
  border: 1px solid #eaeaea;
  border-radius: 0.25rem;
  background-color: white;

  .trust-banner {
    padding: 1rem 1rem 0 1rem;
  }
`

export const PGStatusErrorBanner = styled.div`
  margin: 1rem 1rem 0 1rem;
  & > div {
    align-items: center;
  }
`

export const StyledHeaderContainer = styled.header`
  display: flex;
  flex-wrap: wrap;
  background-color: ${({ isDisabled, isOpened }) =>
    isDisabled || !isOpened ? 'white' : '#0d3578'};
  color: ${({ isDisabled, isOpened }) =>
    isDisabled ? '#6c6c6c' : isOpened ? 'white' : '#333333'};
  opacity: ${({ isDisabled }) => (isDisabled ? '.5' : '1')};
  padding: 1rem;
  position: relative;
  ${({ noCollapsing }) =>
    noCollapsing && 'border-bottom: 0.0625rem solid #eaeaea;'}

  ${SCREEN_SIZE.From.Tablet} {
    border-radius: ${({ isOpened, noCollapsing }) =>
      isOpened || noCollapsing ? '0.25rem 0.25rem 0 0' : '0.25rem'};
  }

  .TangramErrorBanner {
    margin-top: 0.5rem;
    margin-left: 1.75rem;
  }
`

const StyledHeader = styled.div`
  display: flex;
  margin-right: auto;
`

const StyledHeaderText = styled(Text)`
  margin: auto;
  line-height: 1.2rem;
`

const ChangeButton = styled.button`
  margin-left: auto;
  align-self: baseline;
  cursor: pointer;
  border: none;
  outline: none;
  background: transparent;
`

const StyledContent = styled.div`
  transition: max-height 0.5s cubic-bezier(0, 1, 0, 1);
  ${({ isOpened, noCollapsing }) =>
    !isOpened && !noCollapsing && 'overflow: hidden; max-height: 0;'}
`

const InnerPadding = styled.div`
  ${({ noContentPadding }) => !noContentPadding && 'padding: 1rem 1.35rem;'}

  ${SCREEN_SIZE.Only.Mobile} {
    ${({ noContentPadding }) => !noContentPadding && 'padding: 1rem 0.5rem;'}
  }
`

const IconWrapper = styled.div`
  margin: ${({ margin }) => margin || '0.1rem 0.5rem auto 0'};

  > svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`

const FlexBreak = styled.div`
  flex-basis: 100%;
  height: 0;
`

const NotificationBannerAction = styled.div`
  display: inline-flex;
  cursor: pointer;
  justify-content: flex-end;
`

const CustomBanner = styled.div`
  display: flex;
  width: 100%;
  div,
  span {
    flex: 1;
    display: flex;
  }
  svg {
    display: none;
  }
`

class FoldableSection extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      isOpened: this.props.isInitiallyOpened,
    }

    this.handleFoldSection = this.handleFoldSection.bind(this)
  }
  componentDidUpdate(prevProps) {
    if (this.props.isInitiallyOpened !== prevProps.isInitiallyOpened) {
      if (this.props.onOpen && this.props.isInitiallyOpened) {
        this.props.onOpen()
      }
      this.setState({ isOpened: this.props.isInitiallyOpened })
    }
  }

  handleFoldSection(e) {
    if (this.props.isDisabled) {
      e.preventDefault()
      return
    }

    this.setState(
      prevState => ({ isOpened: !prevState.isOpened }),
      () => {
        if (this.state.isOpened) {
          this.props.onExpand && this.props.onExpand()
        } else {
          this.props.onCollapse && this.props.onCollapse()
        }
      }
    )
  }

  render() {
    const {
      children,
      title,
      isDisabled,
      isUnchangeable,
      iconMargin,
      mobileBanner,
      noContentPadding,
      noCollapsing,
      icon: Icon,
      testId,
      tabId,
      track,
      isPGStatusEnabled,
      header,
      body,
    } = this.props

    const { isOpened } = this.state

    return (
      <StyledArticle data-testid={`${testId || 'FoldableSection'}`}>
        {!isOpened &&
        tabId === 'PaymentFoldableSection' &&
        typeof title !== 'string' &&
        !header ? (
          <CheckoutInfoBanner
            selectedCardNumber={title?.props?.paymentData?.selectedCard?.number}
            track={track}
          />
        ) : null}
        {!isOpened &&
        tabId === 'PaymentFoldableSection' &&
        typeof title !== 'string' &&
        isPGStatusEnabled &&
        header &&
        body ? (
          <TangramFairpriceProvider
            experimental={{
              NotificationBanner: true,
              Text: true,
            }}
          >
            <PGStatusErrorBanner>
              <NotificationBanner variant="warning">
                <NotificationBanner.Content>
                  <NotificationBanner.Title>{header}</NotificationBanner.Title>
                  <NotificationBanner.Description>
                    {body}
                  </NotificationBanner.Description>
                </NotificationBanner.Content>
              </NotificationBanner>
            </PGStatusErrorBanner>
          </TangramFairpriceProvider>
        ) : null}
        <StyledHeaderContainer
          isOpened={isOpened}
          isDisabled={isDisabled}
          noCollapsing={noCollapsing}
          data-testid={`${testId || 'FoldableSection'}Header`}
          className={`${isOpened ? 'isOpened' : 'isCollapsed'}`}
        >
          <StyledHeader>
            {Icon && (
              <IconWrapper margin={iconMargin}>
                <Icon
                  color={
                    isDisabled
                      ? 'rgba(51,51,51, 0.5)'
                      : isOpened
                      ? 'white'
                      : '#999999'
                  }
                />
              </IconWrapper>
            )}
            {typeof title === 'string' ? (
              <StyledHeaderText weight="bold" size="large">
                {title}
              </StyledHeaderText>
            ) : (
              title
            )}
          </StyledHeader>
          {!isDisabled && !isOpened && !isUnchangeable && (
            <ChangeButton
              onClick={this.handleFoldSection}
              data-testid="foldable-section-button"
            >
              <Text weight="bold" size="medium" color="#1557bf">
                Change
              </Text>
            </ChangeButton>
          )}
          {this.props.errorBannerText && (
            <>
              <FlexBreak />
              <TangramFairpriceProvider
                experimental={{
                  NotificationBanner: true,
                  Text: true,
                }}
              >
                <CustomBanner
                  data-testid="TangramErrorBanner"
                  className="TangramErrorBanner"
                >
                  <NotificationBanner variant="warning">
                    <NotificationBanner.Content>
                      <NotificationBanner.Description>
                        {this.props.errorBannerText}
                        {this.props.errorBannerActionName && (
                          <NotificationBannerAction
                            data-testid="ErrorBannerAction"
                            onClick={this.handleFoldSection}
                          >
                            {this.props.errorBannerActionName}
                          </NotificationBannerAction>
                        )}
                      </NotificationBanner.Description>
                    </NotificationBanner.Content>
                  </NotificationBanner>
                </CustomBanner>
              </TangramFairpriceProvider>
            </>
          )}
        </StyledHeaderContainer>
        <StyledContent
          isOpened={isOpened}
          className={`${isOpened ? 'isOpened' : 'isCollapsed'}`}
          noCollapsing={noCollapsing}
          data-testid={`${testId || 'FoldableSection'}Content`}
        >
          {mobileBanner && (
            <Media query={SCREEN_SIZE.Below.MobileL}>
              <Image src={mobileBanner} width="100%" />
            </Media>
          )}
          <InnerPadding noContentPadding={noContentPadding}>
            {children}
          </InnerPadding>
        </StyledContent>
      </StyledArticle>
    )
  }
}

export default FoldableSection
